// src/pages/AdminDashboard.js
import React, { useState, useEffect }                  from 'react';
import { useNavigate }                                 from 'react-router-dom';
import { signOut, onAuthStateChanged }                 from 'firebase/auth';
import { doc, onSnapshot, setDoc, serverTimestamp }    from 'firebase/firestore';
import { auth, firebaseConfig, db }                    from '../firebase';

import CreateBatch       from '../components/CreateBatch';
import ViewBatches       from '../components/ViewBatches';
import ViewAllStudents   from '../components/ViewAllStudents';
import Layout from '../components/Layout';

export default function AdminDashboard() {
  const [view, setView]         = useState('');
  const [name, setName]         = useState('');
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [newRole, setNewRole]   = useState('teacher');
  const [msg, setMsg]           = useState('');
  const [err, setErr]           = useState('');
  const [role, setRole]         = useState(null);
  const [userName, setUserName] = useState('');
  const navigate                = useNavigate();

  // Listen for auth state → grab this admin's profile (role + name)
  useEffect(() => {
    const unsubAuth = onAuthStateChanged(auth, user => {
      if (!user) return navigate('/login');
      const unsubSnap = onSnapshot(
        doc(db, 'users', user.uid),
        snap => {
          const data = snap.data() || {};
          setRole(data.role || null);
          setUserName(data.name || '');
        },
        () => {
          setRole(null);
          setUserName('');
        }
      );
      return () => unsubSnap();
    });
    return () => unsubAuth();
  }, [navigate]);

  const handleLogout = async () => {
    await signOut(auth);
    navigate('/login');
  };

  const handleCreateUser = async e => {
    e.preventDefault();
    setMsg(''); setErr('');
    if (!name.trim()) {
      setErr('Name is required');
      return;
    }

    try {
      // 1) Create in Auth via REST
      const res = await fetch(
        `https://identitytoolkit.googleapis.com/v1/accounts:signUp?key=${firebaseConfig.apiKey}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, password }),
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error?.message || 'Auth signup failed');

      // 2) Mirror into Firestore /users/{uid}
      await setDoc(
        doc(db, 'users', data.localId),
        {
          name:      name.trim(),
          email,
          role:      newRole,
          createdAt: serverTimestamp()
        }
      );

      setMsg(`✅ ${newRole.charAt(0).toUpperCase() + newRole.slice(1)} “${name}” created.`);
      setName(''); setEmail(''); setPassword(''); setNewRole('teacher');
    } catch (e) {
      setErr(e.message);
    }
  };

  return (
    <Layout>
    <div className="p-6">
      {/* Header with name + logout */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Admin Dashboard</h1>
        <div className="flex items-center gap-4">
          {userName && <span className="text-gray-800">Hello, {userName}</span>}
          <br />
          <button
            onClick={handleLogout}
           className="px-3 py-1 bg-[#FF6B00] text-white rounded-md hover:bg-[#e65c00]"
          >
            Logout
          </button>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-4 mb-6">
        <button
          onClick={() => setView('create')}
          className="px-4 py-2 bg-[#FF6B00] text-white rounded-md hover:bg-[#e65c00]"
        >
          Create Batch
        </button>
        <button
          onClick={() => setView('batches')}
          className="px-4 py-2 bg-[#FF6B00] text-white rounded-md hover:bg-[#e65c00]"
        >
          View Batches
        </button>
        <button
          onClick={() => setView('students')}
          className="px-4 py-2 bg-[#FF6B00] text-white rounded-md hover:bg-[#e65c00]"
        >
          View All Students
        </button>
        {role === 'admin' && (
          <button
            onClick={() => setView('createUser')}
           className="px-4 py-2 bg-[#FF6B00] text-white rounded-md hover:bg-[#e65c00]"
          >
            Create User
          </button>
        )}
      </div>

      {/* Main Content */}
      <div>
        {view === 'createUser' && (
          <form onSubmit={handleCreateUser} className="space-y-4 max-w-sm">
            <h2 className="text-xl font-semibold">Signup New User</h2>
            <input
              type="text"
              placeholder="Full Name"
              value={name}
              onChange={e => setName(e.target.value)}
              required
               className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF6B00]"
            />
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF6B00]"
            />
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF6B00]"
            />
            <select
              value={newRole}
              onChange={e => setNewRole(e.target.value)}
              className="w-full px-3 py-2 border rounded"
            >
              <option value="teacher">Teacher</option>
              <option value="admin">Admin</option>
            </select>
            <button
              type="submit"
              className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
            >
              Create User
            </button>
            {msg && <p className="text-green-600">{msg}</p>}
            {err && <p className="text-red-600">{err}</p>}
          </form>
        )}

        {view === 'create' && (
          <CreateBatch
            onCreated={() => {
              setView('batches');
            }}
          />
        )}
        {view === 'batches' && <ViewBatches />}
        {view === 'students' && <ViewAllStudents />}

        {!view && !role && (
          <p className="text-gray-600">Loading...</p>
        )}
        {!view && role === 'admin' && (
          <p className="text-gray-600">Select an action above to get started.</p>
        )}
        {!view && role !== 'admin' && (
          <p className="text-gray-600">You do not have access to create users.</p>
        )}
      </div>
    </div>
  </Layout>
  );
}
